/*
 * @providesModule SKZMatchPlayer
 *
 *
 * Created by Skillz on 2/23/16.
 *   Copyright © 2016 Skillz. All rights reserved.
 *
 */

import _ from 'lodash';
import I18n from 'INTL';

import Tournament from 'SKZTournament';
import Match from 'SKZMatch';
import ClientPropertyConstants from 'ClientPropertyConstants';

type MatchPlayerState = 'STARTED' | 'FINISHED' | 'ABORTED' | 'PENDING';
type MatchPlayerOutcome = 'WIN' | 'LOSS' | 'DRAW';

export const WIN: MatchPlayerOutcome = 'WIN';
export const LOSS: MatchPlayerOutcome = 'LOSS';
export const DRAW: MatchPlayerOutcome = 'DRAW';

export class MatchPlayer {
  id: string;
  user_id: number;
  name: string;
  avatar_url: string;
  score: number;
  best_score?: number;
  position: number;
  display_position?: number;
  cash_prize: number;
  bonus_cash_prize: number;
  z_prize: number;
  ticketz_won_prize: number;
  ticketz_prize: number;
  medals_prize: number;
  custom_prize?: {
    name?: string;
    image_url?: string;
    formatted_result_description?: string;
    type?: string;
    ticketz_amount?: number;
  };
  participation_ticketz?: number;
  shoutout: string;
  entry_id: string;
  state: MatchPlayerState;
  flag_url: string;
  thumbnail_url?: string;
  replay_url?: string;
  outcome?: MatchPlayerOutcome;
  finished_at?: string;
  playing_now: boolean;
  is_staff: boolean;
  is_bot: boolean;
  ticketz?: {
    balance: number;
    multiplier: number;
    next_multiplier: number;
    scalable_image: string;
    image: string;
    progress: number;
    goal: number;
    expires_at: string;
  };
  win_percentage: number;
  player_label: string;
  cash_entry_fee?: number;
}

export default class SKZMatchPlayer extends MatchPlayer {
  constructor(keyValueSource?: Object | MatchPlayer) {
    super();
    return _.create(SKZMatchPlayer.prototype, keyValueSource);
  }

  sanitizeTextForScore(displayText: String, match: Match, mainUserId: String): String {
    if (!match.finished_at && !(this.user_id == mainUserId) && match.type != 'TURN_BASED') {
      return I18n.message('COMPLETED');
    }
    return displayText;
  }

  shouldCheckSpectateAvailable(tournament: Tournament) {
    if (ClientPropertyConstants.getSpectateEnabledResults() || tournament?.isLiveBracket()) {
      if (this.state == 'STARTED') {
        if (tournament && tournament.synchronous) {
          return false;
        } else {
          return true;
        }
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  shouldShowSpectate(tournament: Tournament) {
    if (ClientPropertyConstants.getSpectateEnabledResults() || tournament?.isLiveBracket()) {
      if (this.state == 'STARTED') {
        if (tournament && tournament.synchronous) {
          return false;
        } else {
          return true;
        }
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  isPlaceholderByePlayer() {
    if (!this.name && this.state == 'ABORTED') {
      return true;
    } else {
      return false;
    }
  }

  scoreTextForPLOW(tournament: Tournament, match: Match, mainUserId: String): string {
    let scoreText = '';
    if (this.state == 'FINISHED') {
      const formattedScore = I18n.getCurrencyString(this.score, '', true, false);
      scoreText = this.sanitizeTextForScore(formattedScore, match, mainUserId);
    } else if (this.state == 'ABORTED') {
      if (this.isPlaceholderByePlayer()) {
        scoreText = this.sanitizeTextForScore(I18n.message('NO ENTRY'), match, mainUserId);
      } else {
        scoreText = this.sanitizeTextForScore(I18n.message('ABORTED'), match, mainUserId);
      }
    } else if (match.is_rematch) {
      if (this.state == 'STARTED') {
        if (tournament && tournament.synchronous) {
          scoreText = I18n.message('WAITING FOR RESULTS');
        } else {
          scoreText = I18n.message('NOW PLAYING');
        }
      } else {
        scoreText = I18n.message('CHALLENGED');
      }
    } else if (this.state == 'PENDING') {
      scoreText = I18n.message('PENDING');
    } else {
      if (tournament && tournament.synchronous) {
        scoreText = I18n.message('WAITING FOR RESULTS');
      } else {
        scoreText = I18n.message('NOW PLAYING');
      }
    }
    return scoreText;
  }

  getThumbNailURL(): string {
    return this.thumbnail_url || this.avatar_url || 'skillz-generic-avatar';
  }

  getAvatarURL(): string {
    return this.avatar_url || this.thumbnail_url || 'skillz-generic-avatar';
  }

  static Status = {
    ABORTED: 'ABORTED',
    FINISHED: 'FINISHED',
    STARTED: 'STARTED',
  };
}
